// Import base styles (Tailwind)
import './styles/base.css';

// =============================================================================
// NAMED EXPORTS - For ES module consumers: import { Button } from 'hi-ui'
// =============================================================================

// Atoms (alphabetical)
export { Badge } from './atoms/Badge';
export type { BadgeProps, BadgeVariant, BadgeSize } from './atoms/Badge';
export { Banner } from './atoms/Banner';
export type { BannerProps, BannerVariant } from './atoms/Banner';
export { Button } from './atoms/Button';
export type { ButtonProps } from './atoms/Button';
export { CopyButton } from './atoms/CopyButton';
export type { CopyButtonProps } from './atoms/CopyButton';
export { Checkbox } from './atoms/Checkbox';
export type { CheckboxProps } from './atoms/Checkbox';
export { DescriptionList } from './atoms/DescriptionList';
export type {
  DescriptionListProps,
  DescriptionListItem,
  DescriptionListLayout,
  DescriptionListSize,
} from './atoms/DescriptionList';
export { EmptyState } from './atoms/EmptyState';
export type { EmptyStateProps } from './atoms/EmptyState';
export { Errorbox } from './atoms/Errorbox';
export type { ErrorboxProps } from './atoms/Errorbox';
export { Flex } from './atoms/Flex';
export type { FlexProps } from './atoms/Flex';
export { Icon } from './atoms/Icon';
export type { IconProps } from './atoms/Icon';
export { Image } from './atoms/Image';
export type { ImageProps } from './atoms/Image';
export { Input } from './atoms/Input';
export type { InputProps } from './atoms/Input';
export { LayoutContainer } from './atoms/LayoutContainer';
export type { LayoutContainerProps } from './atoms/LayoutContainer';
export { Link } from './atoms/Link';
export type { LinkProps } from './atoms/Link';
export { LoadingOverlay } from './atoms/LoadingOverlay';
export type { LoadingOverlayProps } from './atoms/LoadingOverlay';
export { List } from './atoms/List';
export type { ListProps } from './atoms/List';
export { ListItem } from './atoms/ListItem';
export type { ListItemProps } from './atoms/ListItem';
export { Noticebox } from './atoms/Noticebox';
export type { NoticeboxProps } from './atoms/Noticebox';
export { PriceDisplay } from './atoms/PriceDisplay';
export type { PriceDisplayProps, PriceDisplaySize } from './atoms/PriceDisplay';
export { QuantitySelector } from './atoms/QuantitySelector';
export type { QuantitySelectorProps } from './atoms/QuantitySelector';
export { ProgressBar } from './atoms/ProgressBar';
export type { ProgressBarProps, ProgressBarVariant, ProgressBarSize } from './atoms/ProgressBar';
export { Radio } from './atoms/Radio';
export type { RadioProps } from './atoms/Radio';
export { Select } from './atoms/Select';
export type { SelectProps } from './atoms/Select';
export { Slider } from './atoms/Slider';
export type { SliderProps } from './atoms/Slider';
export { Spinner } from './atoms/Spinner';
export type { SpinnerProps } from './atoms/Spinner';
export { Text } from './atoms/Text';
export type { TextProps } from './atoms/Text';
export { Textarea } from './atoms/Textarea';
export type { TextareaProps } from './atoms/Textarea';
export { Toggle } from './atoms/Toggle';
export type { ToggleProps, ToggleSize } from './atoms/Toggle';
export { Tooltip } from './atoms/Tooltip';
export type { TooltipProps, TooltipPosition } from './atoms/Tooltip';

// Molecules (alphabetical)
export { Accordion, AccordionGroup } from './molecules/Accordion';
export type { AccordionProps, AccordionGroupProps, AccordionItem } from './molecules/Accordion';
export { AccountButton } from './molecules/AccountButton';
export type { AccountButtonProps, DropdownLink } from './molecules/AccountButton';
export { Breadcrumbs } from './molecules/Breadcrumbs';
export type { BreadcrumbsProps, BreadcrumbItem } from './molecules/Breadcrumbs';
export { Card, CardTitle, CardActions } from './molecules/Card';
export type {
  CardProps,
  CardTitleProps,
  CardActionsProps,
  CardVariant,
  CardPadding,
} from './molecules/Card';
export { CardSelector } from './molecules/CardSelector';
export type { CardSelectorProps, CardSelectorOption } from './molecules/CardSelector';
export { CartButton } from './molecules/CartButton';
export type { CartButtonProps } from './molecules/CartButton';
export { ConfirmationDialog } from './molecules/ConfirmationDialog';
export type { ConfirmationDialogProps } from './molecules/ConfirmationDialog';
export { FeatureCard } from './molecules/FeatureCard';
export type { FeatureCardProps, FeatureCardFeature } from './molecules/FeatureCard';
export { FilterableSelect } from './molecules/FilterableSelect';
export type { FilterableSelectProps, FilterableSelectOption } from './molecules/FilterableSelect';
export { Form } from './molecules/Form';
export type { FormProps } from './molecules/Form';
export { FormField } from './molecules/FormField';
export type { FormFieldProps } from './molecules/FormField';
export { InlineEdit } from './molecules/InlineEdit';
export type { InlineEditProps } from './molecules/InlineEdit';
export { MainNavItem } from './molecules/MainNavItem';
export type { MainNavItemProps } from './molecules/MainNavItem';
export { MainNavMobileItem } from './molecules/MainNavMobileItem';
export type { MainNavMobileItemProps } from './molecules/MainNavMobileItem';
export { MenuItem } from './molecules/MenuItem';
export type { MenuItemProps } from './molecules/MenuItem';
export { MenuSection } from './molecules/MenuSection';
export type { MenuSectionProps } from './molecules/MenuSection';
export { Pagination } from './molecules/Pagination';
export type { PaginationProps } from './molecules/Pagination';
export { PaymentMethodSelector } from './molecules/PaymentMethodSelector';
export type { PaymentMethodSelectorProps, PaymentMethod } from './molecules/PaymentMethodSelector';
export { ResumePurchaseBox } from './molecules/ResumePurchaseBox';
export type { ResumePurchaseBoxProps } from './molecules/ResumePurchaseBox';
export { Stepper } from './molecules/Stepper';
export type { StepperProps, StepItem } from './molecules/Stepper';
export { SubNavItem } from './molecules/SubNavItem';
export type { SubNavItemProps } from './molecules/SubNavItem';
export { Table } from './molecules/Table';
export type { TableProps, Column, PaginationConfig } from './molecules/Table';
export { Tabs } from './molecules/Tabs';
export type { TabsProps, TabItem } from './molecules/Tabs';
export { TopNavLink } from './molecules/TopNavLink';
export type { TopNavLinkProps } from './molecules/TopNavLink';

// Organisms (alphabetical)
export { ContactFormModal } from './organisms/ContactFormModal';
export type {
  ContactFormModalProps,
  ContactFormData,
  ContactFormSubmitResult,
  Country,
} from './organisms/ContactFormModal';
export { Footer } from './organisms/Footer';
export type { FooterProps, FooterLinkSection, SocialLink } from './organisms/Footer';
export { MainNav } from './organisms/MainNav';
export type { MainNavProps, MainNavItemData, SubNavItemData } from './organisms/MainNav';
export { Modal } from './organisms/Modal';
export type { ModalProps } from './organisms/Modal';
export { Page } from './organisms/Page';
export type { PageProps } from './organisms/Page';
export { Sidebar } from './organisms/Sidebar';
export type { SidebarProps } from './organisms/Sidebar';
export { TopNav } from './organisms/TopNav';
export type { TopNavProps } from './organisms/TopNav';

// Pages (alphabetical)
export { BasketPage } from './pages/BasketPage';
export type { BasketPageProps, BasketItemData, EmptyBasketLink } from './pages/BasketPage';
export { RegisterPage } from './pages/RegisterPage';
export type { RegisterPageProps, CountryOption } from './pages/RegisterPage';
export { SoftTransferPage } from './pages/SoftTransferPage';
export type { SoftTransferPageProps } from './pages/SoftTransferPage';
export { TicketListPage } from './pages/TicketListPage';
export type { TicketListPageProps } from './pages/TicketListPage';
export { TicketRaisePage } from './pages/TicketRaisePage';
export type {
  TicketRaisePageProps,
  ServiceOption,
  ExpertServiceOption,
  SystemStatusMessage,
  SupportArticle,
} from './pages/TicketRaisePage';
export { TicketViewPage } from './pages/TicketViewPage';
export type {
  TicketViewPageProps,
  TicketUpdate,
  TicketUpdateAttachment,
  TicketUpdateQuote,
} from './pages/TicketViewPage';

// Helper mount functions - for legacy template integration
// Only nav shell and page components are exported; other components
// are composed within page components in React
export {
  mountBreadcrumbs,
  mountFooter,
  mountMainNav,
  mountPage,
  mountRegisterPage,
  mountSidebar,
  mountSoftTransferPage,
  mountTicketListPage,
  mountTicketRaisePage,
  mountTicketViewPage,
  mountTopNav,
  unmount,
} from './helpers/mount';

// =============================================================================
// DEFAULT EXPORT - For UMD consumers: window.HIUI.Button
// =============================================================================

// Atoms
import { Badge } from './atoms/Badge';
import { Banner } from './atoms/Banner';
import { Button } from './atoms/Button';
import { CopyButton } from './atoms/CopyButton';
import { Checkbox } from './atoms/Checkbox';
import { DescriptionList } from './atoms/DescriptionList';
import { EmptyState } from './atoms/EmptyState';
import { Errorbox } from './atoms/Errorbox';
import { Flex } from './atoms/Flex';
import { Icon } from './atoms/Icon';
import { Image } from './atoms/Image';
import { Input } from './atoms/Input';
import { LayoutContainer } from './atoms/LayoutContainer';
import { Link } from './atoms/Link';
import { LoadingOverlay } from './atoms/LoadingOverlay';
import { List } from './atoms/List';
import { ListItem } from './atoms/ListItem';
import { Noticebox } from './atoms/Noticebox';
import { PriceDisplay } from './atoms/PriceDisplay';
import { QuantitySelector } from './atoms/QuantitySelector';
import { ProgressBar } from './atoms/ProgressBar';
import { Radio } from './atoms/Radio';
import { Select } from './atoms/Select';
import { Slider } from './atoms/Slider';
import { Spinner } from './atoms/Spinner';
import { Text } from './atoms/Text';
import { Textarea } from './atoms/Textarea';
import { Toggle } from './atoms/Toggle';
import { Tooltip } from './atoms/Tooltip';

// Molecules
import { Accordion, AccordionGroup } from './molecules/Accordion';
import { AccountButton } from './molecules/AccountButton';
import { Breadcrumbs } from './molecules/Breadcrumbs';
import { Card, CardTitle, CardActions } from './molecules/Card';
import { CardSelector } from './molecules/CardSelector';
import { CartButton } from './molecules/CartButton';
import { ConfirmationDialog } from './molecules/ConfirmationDialog';
import { FeatureCard } from './molecules/FeatureCard';
import { FilterableSelect } from './molecules/FilterableSelect';
import { Form } from './molecules/Form';
import { FormField } from './molecules/FormField';
import { InlineEdit } from './molecules/InlineEdit';
import { MainNavItem } from './molecules/MainNavItem';
import { MainNavMobileItem } from './molecules/MainNavMobileItem';
import { MenuItem } from './molecules/MenuItem';
import { MenuSection } from './molecules/MenuSection';
import { Pagination } from './molecules/Pagination';
import { PaymentMethodSelector } from './molecules/PaymentMethodSelector';
import { ResumePurchaseBox } from './molecules/ResumePurchaseBox';
import { Stepper } from './molecules/Stepper';
import { SubNavItem } from './molecules/SubNavItem';
import { Table } from './molecules/Table';
import { Tabs } from './molecules/Tabs';
import { TopNavLink } from './molecules/TopNavLink';

// Organisms
import { ContactFormModal } from './organisms/ContactFormModal';
import { Footer } from './organisms/Footer';
import { MainNav } from './organisms/MainNav';
import { Modal } from './organisms/Modal';
import { Page } from './organisms/Page';
import { Sidebar } from './organisms/Sidebar';
import { TopNav } from './organisms/TopNav';

// Pages
import { BasketPage } from './pages/BasketPage';
import { RegisterPage } from './pages/RegisterPage';
import { SoftTransferPage } from './pages/SoftTransferPage';
import { TicketListPage } from './pages/TicketListPage';
import { TicketRaisePage } from './pages/TicketRaisePage';
import { TicketViewPage } from './pages/TicketViewPage';

// Mount functions
import {
  mountBreadcrumbs,
  mountFooter,
  mountMainNav,
  mountPage,
  mountRegisterPage,
  mountSidebar,
  mountSoftTransferPage,
  mountTicketListPage,
  mountTicketRaisePage,
  mountTicketViewPage,
  mountTopNav,
  unmount,
} from './helpers/mount';

export default {
  // Atoms (alphabetical)
  Badge,
  Banner,
  Button,
  CopyButton,
  Checkbox,
  DescriptionList,
  EmptyState,
  Errorbox,
  Flex,
  Icon,
  Image,
  Input,
  LayoutContainer,
  Link,
  LoadingOverlay,
  List,
  ListItem,
  Noticebox,
  PriceDisplay,
  QuantitySelector,
  ProgressBar,
  Radio,
  Select,
  Slider,
  Spinner,
  Text,
  Textarea,
  Toggle,
  Tooltip,

  // Molecules (alphabetical)
  Accordion,
  AccordionGroup,
  AccountButton,
  Breadcrumbs,
  Card,
  CardActions,
  CardSelector,
  CardTitle,
  CartButton,
  ConfirmationDialog,
  FeatureCard,
  FilterableSelect,
  Form,
  FormField,
  InlineEdit,
  MainNavItem,
  MainNavMobileItem,
  MenuItem,
  MenuSection,
  Pagination,
  PaymentMethodSelector,
  ResumePurchaseBox,
  Stepper,
  SubNavItem,
  Table,
  Tabs,
  TopNavLink,

  // Organisms (alphabetical)
  ContactFormModal,
  Footer,
  MainNav,
  Modal,
  Page,
  Sidebar,
  TopNav,

  // Pages (alphabetical)
  BasketPage,
  RegisterPage,
  SoftTransferPage,
  TicketListPage,
  TicketRaisePage,
  TicketViewPage,

  // Mount functions (nav shell + pages only)
  mountBreadcrumbs,
  mountFooter,
  mountMainNav,
  mountPage,
  mountRegisterPage,
  mountSidebar,
  mountSoftTransferPage,
  mountTicketListPage,
  mountTicketRaisePage,
  mountTicketViewPage,
  mountTopNav,
  unmount,
};
